import { useTranslation } from "react-i18next";

import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { countries_options } from "@/components/Form/CountryPhone/data";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import {
  DEFAULT_ASSET_CLASS_OPTIONS,
  VALUATION_METHOD_OPTIONS,
} from "./edit-form-conf";
import { SHOCK_TYPE_OPTIONS } from "./shock_type_options";

const filterM2StaticDatumsSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
  custodian_contains: z.string().optional(),
  custodian_code_contains: z.string().optional(),
  custodian_code_type_equals: z.string().optional(),
  custodian_country_code_equals: z.string().optional(),
  climate_risk_equals: z.string().optional(),
  valuation_method_equals: z.string().optional(),
  shock_type_contains: z.string().optional(),
});

export type FilterM2StaticDatumsSchema = z.infer<
  typeof filterM2StaticDatumsSchema
>;

const TABLE_NAME = Tables.M2_STATIC_DATA;

export function FilterM2StaticDatums() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: "name_contains",
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Name" }),
      type: "asyncAutocomplete",
      base_url: "m2/m2_static_data",
      column_name: "name",
    },
    {
      name: "isin_contains",
      label: t(`${TABLE_NAME}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: "m2/m2_static_data",
      column_name: "isin",
    },
    {
      name: "code_contains",
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: "m2/m2_static_data",
      column_name: "code",
    },
    {
      name: "ticker_contains",
      label: t(`${TABLE_NAME}.searchbox.ticker_contains`, { defaultValue: "Ticker" }),
      type: "asyncAutocomplete",
      base_url: "m2/m2_static_data",
      column_name: "ticker",
    },
    {
      name: "currency_contains",
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: "asset_class_1_equals",
      assetSubclassFieldName: "asset_class_2_equals",
      assetClassLabel: t(`${TABLE_NAME}.searchbox.asset_class_1_equals`, { defaultValue: "Asset class" }),
      assetSubclassLabel: t(`${TABLE_NAME}.searchbox.asset_class_2_equals`, { defaultValue: "Sub asset class" }),
      client: Clients.M2,
      staticDataTableName: TABLE_NAME,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
    {
      name: "custodian_contains",
      label: t(`${TABLE_NAME}.columns.custodian`, { ns: "tables", defaultValue: "Custodian" }),
      type: "input",
    },
    {
      name: "custodian_code_contains",
      label: t(`${TABLE_NAME}.columns.custodian_code`, { ns: "tables", defaultValue: "Custodian code" }),
      type: "input",
    },
    {
      name: "custodian_code_type_equals",
      label: t(`${TABLE_NAME}.columns.custodian_code_type`, { ns: "tables", defaultValue: "Custodian code type" }),
      type: "select",
      useTranslation: true,
      client: Clients.M2,
      table_name: TABLE_NAME,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "custodian_code_type",
    },
    {
      name: "custodian_country_code_equals",
      label: t(`${TABLE_NAME}.columns.custodian_country_code`, { ns: "tables", defaultValue: "Custodian country" }),
      type: "autocomplete",
      options: countries_options,
    },
    {
      name: "climate_risk_equals",
      label: t(`${TABLE_NAME}.columns.climate_risk`, { ns: "tables", defaultValue: "Climate risk" }),
      type: "select",
      useTranslation: true,
      client: Clients.M2,
      table_name: TABLE_NAME,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "climate_risk",
    },
    {
      name: "valuation_method_equals",
      label: t(`${TABLE_NAME}.columns.valuation_method`, { ns: "tables", defaultValue: "Valuation method" }),
      type: "select",
      useTranslation: false,
      options: VALUATION_METHOD_OPTIONS,
    },
    {
      name: "shock_type_contains",
      label: t(`${TABLE_NAME}.columns.shock_type`, { ns: "tables", defaultValue: "Shock type" }),
      type: "autocomplete",
      options: SHOCK_TYPE_OPTIONS,
    },
  ];

  return (
    <FilterTable
      schema={filterM2StaticDatumsSchema}
      client={Clients.M2}
      tableName={TABLE_NAME}
      fields={fields}
      columns={2}
    />
  );
}
